import courseService from '@/services/course';
import {getLocalData, saveLocalData} from '@/lib/base/localData';
import i18n from '@/i18n/index.js';
import Vue from 'vue';
import {TAGS} from '@/constant/coursePlan';

/**
 * @returns {Object} initialize state
 */
const initialState = () => {
  return {
    courses: null,
    echelon: null,
    hasKgp: false,
    plans: [],
    anchorDay: null,
    currentPlan: {},
    plansList: [],
  };
};

const state = initialState();

const getters = {
  currentCourses(state) {
    return state.courses;
  },
  echelon(state) {
    return state.echelon;
  },
  currentPlan(state) {
    return state.currentPlan;
  },
  plans(state) {
    return state.plans;
  },
  noPlans(state) {
    return state.plans?.length === 0;
  },
  isCurrentPlan(state) {
    return (planId) => {
      return state.currentPlan.id === planId;
    };
  },
  kgpCount(state) {
    return state.plans.filter((plan) => plan?.tags?.includes(TAGS.KGP)).length;
  },
  tgcCount(state) {
    return state.plans.filter((plan) => plan?.tags?.includes(TAGS.TGC)).length;
  },
  hasKgp(state) {
    return state.plans.some((plan) => plan?.tags?.includes(TAGS.KGP));
  },
  hasTgc(state) {
    return state.plans.some((plan) => plan?.tags?.includes(TAGS.TGC));
  },
  anchorDay(state) {
    return state.anchorDay;
  },
  favoritePlans(state) {
    return (state.plans || []).filter((plan) => plan.isFavorite);
  },
  getPlanTypes: () => (options) => {
    const types = [];
    if (options.isKid) {
      types.push('kid');
    }
    switch (options.stage) {
      case 'Beginner':
        types.push('beginner');
        break;
      case 'Intermediate':
        types.push('intermediate');
        break;
      case 'Advanced':
        types.push('advanced');
        break;
      case 'Mastery':
        types.push('mastery');
        break;
    }
    return types;
  },
  getPlanTypesName: () => (planType) => {
    let typeName = '';
    if (planType.includes('kid')) {
      typeName += '兒童';
    }
    switch (true) {
      case planType.includes('beginner'):
        typeName += '基礎';
        break;
      case planType.includes('intermediate'):
        typeName += '進階';
        break;
      case planType.includes('advanced'):
        typeName += '高階';
        break;
      case planType.includes('mastery'):
        typeName += '大師';
        break;
    }
    return i18n.t(typeName);
  },
};

const actions = {
  getCourses({commit}, currentPlanId, isAll = true) {
    return courseService.getCourses(currentPlanId, isAll).then((res) => {
      commit('setCourses', res.courses);
      commit('setEchelon', res.echelon);
    });
  },
  async getPlansList({commit, getters}) {
    const plans = await courseService.getCoursePlans();

    if (plans) {
      plans.forEach((plan) => {
        plan.types = getters.getPlanTypes({
          isKid: plan.isKid,
          stage: plan.igqeStage,
        });
        plan.typeName = getters.getPlanTypesName(plan.types);
      });
      commit('setPlansList', plans);
    }
  },
  getPlans({commit}, username) {
    return courseService.getCoursePlans(username).then((plans) => {
      const planList = plans.filter((plan) =>
        plan.tags?.includes(TAGS.SHOW_IN_LIST)
      );
      const localPlanInfo = JSON.parse(getLocalData('plan-info'));

      let planInfo;

      // 優先檢查是否有新購買的課程（isNew: true）
      const newPlans = planList.filter((plan) => plan.isNew);
      if (newPlans.length > 0) {
        // 如果有多個新課程，選擇 startedAt 最大的（最新購買的）
        planInfo = newPlans.sort(
          (a, b) => (b.startedAt || 0) - (a.startedAt || 0)
        )[0];
      } else {
        // 沒有新課程時，使用原有邏輯
        planInfo = planList.find((plan) => {
          return localPlanInfo?.id === plan.id;
        });

        if (!planInfo) {
          planInfo = planList[0] || {};
        }
      }

      commit('setPlans', plans);
      commit('setCurrentPlan', planInfo);
      saveLocalData('plan-info', JSON.stringify(planInfo));
    });
  },
  toggleFavorite({commit}, course) {
    courseService.setCourseFavorite(course.id, !course.isFavorite);
    commit('setIsFavorite', course);
  },
  markNewCoursesAsRead({commit, state}) {
    // 找出所有 isNew 為 true 的課程
    const newCourses = state.plans.filter((plan) => plan.isNew);
    if (newCourses.length > 0) {
      // 為每個新課程調用 API 標記為已讀
      newCourses.forEach((course) => {
        courseService.setCourseFavorite(
          course.id,
          course.isFavorite || false,
          false
        );
      });
      commit('markAllAsRead');
    }
  },
};

const mutations = {
  setPlansList(state, plans) {
    state.plansList = plans;
  },
  setCourses(state, courses) {
    state.courses = courses;
  },
  setIsFavorite(state, course) {
    const mapPlan = state.plans.find((plan) => plan.id === course.id);
    const newFavoriteStatus = !course.isFavorite;
    Vue.set(mapPlan, 'isFavorite', newFavoriteStatus);
    // 如果是標記為最愛，更新 setFavoriteAt 為當前時間
    if (newFavoriteStatus) {
      Vue.set(mapPlan, 'setFavoriteAt', Date.now());
    }
  },
  setEchelon(state, echelon) {
    state.echelon = echelon;
  },
  setPlans(state, plans) {
    state.plans = plans;
  },
  setCurrentPlan(state, plan) {
    state.currentPlan = plan;
  },
  setAnchorDay(state, day) {
    state.anchorDay = day;
  },
  markAllAsRead(state) {
    state.plans.forEach((plan) => {
      if (plan.isNew) {
        Vue.set(plan, 'isNew', false);
      }
    });
  },
  resetState(state) {
    const _state = initialState();
    Object.assign(state, _state);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
